import { Button } from "@chakra-ui/button";
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Input } from "@chakra-ui/input";
import { VStack, Text, HStack } from "@chakra-ui/layout";
import { useToast } from "@chakra-ui/toast";
import axios from "axios";
import { useEffect, useRef, useState } from "react";

const RESEND_COOLDOWN = 60;

// Reusable OTP step: used by Signup (post-register) and Login
// (blocked-unverified). Parent owns navigation/storage via onVerified.
const VerifyOtp = ({ email, devPreviewOtp, onVerified, onBack }) => {
  const toast = useToast();
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const [preview, setPreview] = useState(devPreviewOtp || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setPreview(devPreviewOtp || "");
  }, [devPreviewOtp]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(
      () => setCooldown((c) => (c > 0 ? c - 1 : 0)),
      1000
    );
    return () => clearInterval(id);
  }, [cooldown]);

  const handleOtpChange = (value) => {
    setOtp(value.replace(/\D/g, "").slice(0, 6));
  };

  const verify = async (e) => {
    if (e) e.preventDefault();
    if (otp.length !== 6) {
      toast({
        title: "Enter the 6-digit code",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/api/user/verify-otp", {
        email,
        otp,
      });
      toast({
        title: "Email verified ✓",
        description: `Welcome${data.name ? `, ${data.name.split(" ")[0]}` : ""}. You're in.`,
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
      if (onVerified) onVerified(data);
    } catch (error) {
      const payload = error.response?.data || {};
      toast({
        title: "Verification failed",
        description: payload.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      if (payload.code === "OTP_EXPIRED" || payload.code === "OTP_LOCKED") {
        setCooldown((c) => (c > 0 ? c : 0));
      }
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      const { data } = await axios.post("/api/user/resend-otp", { email });
      if (data.devPreviewOtp) setPreview(data.devPreviewOtp);
      else setPreview("");
      toast({
        title: data.emailSent === false ? "Code regenerated" : "New code sent",
        description:
          data.emailSent === false
            ? "Email service hiccup — use the new code below (dev) or try again."
            : `Check ${email} for the fresh 6-digit code.`,
        status: data.emailSent === false ? "warning" : "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setCooldown(RESEND_COOLDOWN);
    } catch (error) {
      const payload = error.response?.data || {};
      if (payload.retryAfter) setCooldown(payload.retryAfter);
      toast({
        title: "Couldn't resend yet",
        description: payload.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <VStack spacing="12px" align="stretch" as="form" onSubmit={verify}>
      <Text fontSize="13px" color="gray.300" lineHeight="20px">
        We sent a <b>6-digit code</b> to <b style={{ color: "#fff" }}>{email}</b>.
        Enter it below — it expires in 10 minutes.
      </Text>
      <FormControl isRequired>
        <FormLabel className="auth-label">Verification code</FormLabel>
        <Input
          ref={inputRef}
          className="auth-input"
          value={otp}
          onChange={(e) => handleOtpChange(e.target.value)}
          placeholder="••••••"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          textAlign="center"
          letterSpacing="8px"
          fontSize="22px"
          fontWeight="800"
        />
      </FormControl>
      {preview && (
        <Text
          fontSize="12px"
          color="yellow.200"
          bg="whiteAlpha.100"
          borderRadius="8px"
          p="8px 12px"
        >
          Dev preview (no SMTP configured): <b>{preview}</b>
        </Text>
      )}
      <Button
        className="auth-submit"
        width="100%"
        style={{ marginTop: 8 }}
        type="submit"
        isLoading={loading}
        loadingText="Verifying…"
      >
        Verify Email →
      </Button>
      <HStack justify="space-between" align="center" pt="2px">
        <Button
          variant="link"
          size="sm"
          colorScheme="cyan"
          onClick={resend}
          isLoading={resending}
          isDisabled={cooldown > 0}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </Button>
        {onBack && (
          <Button variant="link" size="sm" colorScheme="gray" onClick={onBack}>
            ← Use a different email
          </Button>
        )}
      </HStack>
    </VStack>
  );
};

export default VerifyOtp;
